// wsCloseMessage — a WebSocket close code, said in words.
//
// Andres, on gass: the disconnect toast read literally
// `{"code":1006,"reason":""}`, twice in one session. 1006 is "abnormal
// closure, no close frame received", so the browser never gets a reason
// string — which makes that payload the single least actionable text we
// could have put on screen. The user cannot act on a number, and the one
// field that might have carried meaning is empty by definition for the code
// that fires most.
//
// The toast is produced by the blueprint's `print` node walking up to the
// `onWSDisconnected` payload (every shipped blueprint wires
// ev_ws_disconnected -> act_print_disconnect with no `text`), so the fix is
// to give that payload a display string rather than to re-author four
// blueprints and every character's stored graph. `code`/`reason` stay on the
// payload for the graph's data pins and for the console.
//
// "reconnecting" is a claim, so it is checked: ConvorcherClient's `onclose`
// calls `_scheduleReconnect()`, which retries with exponential backoff up to
// `_maxReconnectAttempts` (10). A deliberate `disconnect()` detaches
// `onclose` first, so it never reaches this path.

/** Known close codes → what actually happened, in the user's terms.
 *  Deliberately no code in the text: the code is diagnosis, not headline. */
const KNOWN = {
  1000: 'The conversation service closed the connection — reconnecting',
  1001: 'The conversation service went away — reconnecting',
  1005: 'Lost connection to the conversation service — reconnecting',
  1006: 'Lost connection to the conversation service — reconnecting',
  1011: 'The conversation service hit an error — reconnecting',
  1012: 'The conversation service is restarting — reconnecting',
  1013: 'The conversation service is busy — retrying shortly',
  1015: 'Could not establish a secure connection to the conversation service — retrying',
}

/**
 * Describe a WebSocket close for a human.
 *
 * @param {number}  code    the CloseEvent code
 * @param {string} [reason] the server-supplied reason, usually '' in practice
 * @returns {string} one sentence, safe to show as-is
 */
export function describeWebSocketClose(code, reason) {
  const n = Number(code)
  // An unknown code has nothing to translate, so the number IS the only fact
  // worth reporting — the one case where it belongs in the text.
  const base = KNOWN[n]
    ?? (Number.isFinite(n)
      ? `Connection to the conversation service closed (code ${n}) — reconnecting`
      : 'Connection to the conversation service closed — reconnecting')

  // A non-empty reason is the server bothering to explain itself; keep it,
  // after the plain-language headline rather than instead of it.
  const detail = typeof reason === 'string' ? reason.trim() : ''
  return detail ? `${base} (${detail})` : base
}
